/**
 * Standard shape for the values an action returns to the client.
 *
 * Every action that returns data (rather than redirecting) speaks the same
 * tagged union: a flat `ok` discriminant plus action-specific fields. The
 * `actionOk` / `actionError` builders are the only sanctioned way to produce
 * one — they guarantee the discriminant is set, that the payload is wrapped in
 * `data()` so a status code and headers can always be attached, and that
 * failures default to HTTP 400. This keeps `useActionData` / `fetcher.data`
 * predictable: consumers branch on `ok` and never have to guess whether a
 * given action wrapped its result or set a status.
 *
 * Redirect-based (POST-redirect-GET) actions are the deliberate exception:
 * they advance the flow and return a `Response` from `redirect()` /
 * `redirectDocument()` instead of an `ActionResult`. Actions that stay on the
 * same page (validation feedback, inline mutations) return an `ActionResult`.
 *
 * This module is client-safe — `data` is part of the React Router runtime and
 * the narrowing helpers are pure — so components may import the guards without
 * pulling in server-only code.
 */
import { data } from 'react-router';

/** The success arm: `{ ok: true }` plus any action-specific fields. */
export type ActionSuccess<TOk extends object = object> = { ok: true } & TOk;

/** The failure arm: `{ ok: false }` plus any action-specific fields. */
export type ActionFailure<TErr extends object = object> = { ok: false } & TErr;

/** The full discriminated union an on-page action returns. */
export type ActionResult<
  TOk extends object = object,
  TErr extends object = object,
> = ActionSuccess<TOk> | ActionFailure<TErr>;

type ResultInit = { status?: number; headers?: HeadersInit };

/**
 * Build a success result: `{ ok: true, ...payload }`, wrapped in `data()` so a
 * status (default 200) and headers (e.g. `Set-Cookie`) can be attached.
 */
export function actionOk<const TOk extends object>(
  payload: TOk = {} as TOk,
  init?: ResultInit
) {
  return data({ ok: true as const, ...payload }, init);
}

/**
 * Build a failure result: `{ ok: false, ...payload }`, wrapped in `data()` with
 * a default HTTP 400. Pass `init.status` to override (e.g. 500 for an upstream
 * failure the client should still see).
 */
export function actionError<const TErr extends object>(
  payload: TErr,
  init?: ResultInit
) {
  return data({ ok: false as const, ...payload }, { status: 400, ...init });
}

/**
 * Client-side guard: narrow `useActionData()` / `fetcher.data` (typed `unknown`
 * across the network boundary) to the failure arm. Saves every consumer from
 * re-implementing the `typeof === 'object' && 'ok' in …` dance.
 */
export function isActionFailure(
  value: unknown
): value is ActionFailure<Record<string, unknown>> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'ok' in value &&
    (value as { ok: unknown }).ok === false
  );
}

/** Client-side guard: narrow action data to the success arm. */
export function isActionSuccess(
  value: unknown
): value is ActionSuccess<Record<string, unknown>> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'ok' in value &&
    (value as { ok: unknown }).ok === true
  );
}
